import type { FeedbackKind } from "odyssey-shared";
import { say } from "../../words.ts";

/**
 * Sending a report from the game (ADR-081's amendment of 2026-10-05): the body the gateway's `/requests` and the
 * dev server's request endpoint both take, the post, and what its answer means for the form. A stored report is
 * known by the first eight characters of its id. A refusal is the server's own sentence, shown as text; a refusal
 * that says the account's feedback is paused or off also tells the page, so the menu's entry follows. Nothing
 * here touches the page: the caller hands in the fetch, the address and what the page reads.
 */

/**
 * An account's access to feedback as the page knows it: open, paused until a moment on the page's clock, or off.
 */
export type FeedbackAccess = { readonly state: "open" } | { readonly state: "paused"; readonly until: number } | { readonly state: "off" };

/**
 * What the form hands over to send.
 */
export interface FeedbackDraft {
  readonly kind: FeedbackKind;
  readonly title: string;
  readonly description: string;
  readonly withLog: boolean;
  readonly withScreenshot: boolean;
}

/**
 * A fetch as the sender calls it.
 */
export type FeedbackFetch = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Where a report goes and what the page adds to it.
 */
export interface FeedbackDestination {
  /** The address reports are posted to. */
  readonly endpoint: string;
  /** The client tag: `live-game` to the gateway, `game` to the dev server. */
  readonly client: string;
  /** The fetch: the session token's for the gateway, the dev server's own for a development page. */
  readonly fetcher: FeedbackFetch;
  /** The page's address. */
  readonly url: () => string;
  /** The browser's user agent. */
  readonly userAgent: () => string;
  /** The world version the page shows, or null. */
  readonly worldVersion: () => string | null;
  /** The page's log. */
  readonly log: () => string;
  /** A PNG data URL of the game, or null when there is nothing to capture. */
  readonly screenshot: () => Promise<string | null>;
  /** The page's clock, in milliseconds. */
  readonly now: () => number;
}

/**
 * What came of a send: the report's reference, or the sentence to show and, when the answer named one, the
 * account's access.
 */
export type SendOutcome = { readonly ok: true; readonly reference: string } | { readonly ok: false; readonly message: string; readonly access: FeedbackAccess | null };

/**
 * The most characters of the server's sentence the form shows.
 */
export const SENTENCE_CHARS = 200;

/**
 * How many characters of a report's id make its reference.
 */
export const REFERENCE_CHARS = 8;

/**
 * The header a refusal names the account's access in.
 */
const ACCESS_HEADER = "X-Feedback-Access";

/**
 * The access a refusal names, if any.
 *
 * @param response The refusal.
 * @param now The page's clock, in milliseconds.
 * @returns Paused until `Retry-After` seconds from now (a minute when the header is missing), off, or null when
 *   the answer names neither.
 */
function accessOf(response: Response, now: number): FeedbackAccess | null {
  const named = response.headers.get(ACCESS_HEADER);
  if (named === "off") {
    return { state: "off" };
  }
  if (named !== "paused") {
    return null;
  }
  const seconds = Number(response.headers.get("Retry-After"));
  return { state: "paused", until: now + (Number.isFinite(seconds) && seconds > 0 ? seconds : 60) * 1000 };
}

/**
 * The first line of an answer's text, clipped, or the fallback when it holds none.
 *
 * @param text The answer's body.
 * @returns The sentence: the answer's own, or `feedback.notAccepted` when it holds none.
 */
function sentenceOf(text: string): string {
  const line = (text.split("\n")[0] ?? "").trim();
  return line === "" ? say("feedback.notAccepted") : line.slice(0, SENTENCE_CHARS);
}

/**
 * Sends one report.
 *
 * @param destination Where it goes and what the page adds.
 * @param draft What the form holds.
 * @returns The reference of the stored report, or the sentence to show; never rejects.
 * @example
 * const outcome = await sendFeedback(destination, { kind: "bug", title: "Stuck on a fence", description: "", withLog: true, withScreenshot: false });
 */
export async function sendFeedback(destination: FeedbackDestination, draft: FeedbackDraft): Promise<SendOutcome> {
  let response: Response;
  let text: string;
  try {
    const body = {
      client: destination.client,
      kind: draft.kind,
      title: draft.title.trim(),
      description: draft.description.trim(),
      url: destination.url(),
      userAgent: destination.userAgent(),
      worldVersion: destination.worldVersion(),
      console: draft.withLog ? destination.log() : "",
      screenshot: draft.withScreenshot ? await destination.screenshot() : null,
    };
    response = await destination.fetcher(destination.endpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    text = await response.text();
  } catch {
    return { ok: false, message: say("feedback.notSent"), access: null };
  }
  if (!response.ok) {
    return { ok: false, message: sentenceOf(text), access: accessOf(response, destination.now()) };
  }
  let id: unknown;
  try {
    id = (JSON.parse(text) as { id?: unknown }).id;
  } catch {
    id = undefined;
  }
  return typeof id === "string" && id !== "" ? { ok: true, reference: id.slice(0, REFERENCE_CHARS) } : { ok: false, message: say("feedback.notAccepted"), access: null };
}
